import '../styles/NewCrawlTargetForm.css';
import React, { useEffect, useId, useRef, useState } from 'react';
import { projectData } from '../lib/projectData';

const MAX_TARGETS = 10;

function looksLikeTarget(value) {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/\s/.test(trimmed)) return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (err) {
    return false;
  }
}

function RuleMark() {
  return (
    <svg viewBox="0 0 44 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" aria-hidden="true">
      <path d="M2 11 L12 5 L22 12 L32 4 L42 9" />
    </svg>
  );
}

export function NewCrawlTargetForm() {
  const baseId = useId();
  const nextKey = useRef(2);

  const [targets, setTargets] = useState(() =>
    [{ key: 't1', value: '' }].map((row) => ({ ...row, error: '' }))
  );
  const [formError, setFormError] = useState('');
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | submitting | success
  const [serverError, setServerError] = useState('');

  const submitting = submitStatus === 'submitting';

  useEffect(() => {
    if (submitStatus !== 'success') return undefined;
    const timer = window.setTimeout(() => {
      window.location.assign('/crawls');
    }, 700);
    return () => window.clearTimeout(timer);
  }, [submitStatus]);

  const updateTarget = (key, value) => {
    setTargets((rows) =>
      rows.map((row) => (row.key === key ? { ...row, value, error: '' } : row))
    );
    setServerError('');
    setFormError('');
  };

  const addTarget = () => {
    if (targets.length >= MAX_TARGETS) return;
    const key = `t${nextKey.current}`;
    nextKey.current += 1;
    setTargets((rows) => [...rows, { key, value: '', error: '' }]);
    setFormError('');
  };

  const removeTarget = (key) => {
    setTargets((rows) => (rows.length <= 1 ? rows : rows.filter((row) => row.key !== key)));
    setServerError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setServerError('');
    setFormError('');

    const filled = targets.filter((row) => row.value.trim() !== '');
    if (filled.length === 0) {
      setFormError('Add at least one starting web page or web directory location.');
      return;
    }

    const validated = targets.map((row) => {
      const value = row.value.trim();
      if (!value) return { ...row, error: '' };
      if (!looksLikeTarget(value)) {
        return { ...row, error: 'Enter a full URL starting with http:// or https:// and no spaces.' };
      }
      return { ...row, error: '' };
    });

    const invalidRows = validated.filter((row) => row.error);
    const normalized = validated
      .filter((row) => row.value.trim() !== '')
      .map((row) => row.value.trim());

    const seen = new Set();
    const withDuplicates = validated.map((row) => {
      const value = row.value.trim();
      if (!value || row.error) return row;
      if (seen.has(value)) {
        return { ...row, error: 'This target is already in the list.' };
      }
      seen.add(value);
      return row;
    });

    setTargets(withDuplicates);

    if (invalidRows.length > 0 || withDuplicates.some((row) => row.error)) {
      setFormError('Check the highlighted targets and try again.');
      const firstRow = withDuplicates.find(
        (row) => row.error || invalidRows.some((bad) => bad.key === row.key)
      );
      const firstInvalid = firstRow ? document.getElementById(`${baseId}-target-${firstRow.key}`) : null;
      if (firstInvalid) firstInvalid.focus();
      return;
    }

    setSubmitStatus('submitting');
    try {
      const author = 'researcher@example.com';
      const now = new Date().toISOString();
      for (const target of normalized) {
        await projectData.create('crawls', {
          id: `crawl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
          target,
          createdAt: now,
          owner: author,
          status: 'queued',
          progress: 0,
          emailCount: 0,
          updatedAt: now,
        });
      }
      setSubmitStatus('success');
    } catch (err) {
      setSubmitStatus('idle');
      setServerError(
        (err && err.message) ||
          'The crawl could not be created. Your targets are kept — try again.'
      );
    }
  };

  const targetCount = targets.filter((row) => row.value.trim() !== '').length;
  const displayedCount = targetCount === 1 ? '1 target' : `${targetCount} targets`;

  return (
    <section className="nctf-root" aria-labelledby={`${baseId}-title`}>
      <div className="nctf-rail" data-content-rail="primary">
        <div className="nctf-panel">
          <header className="nctf-head">
            <span className="nctf-eyebrow">
              <RuleMark />
              Crawl targets
            </span>
            <h2 className="nctf-title" id={`${baseId}-title`}>
              Where should the crawl start?
            </h2>
            <p className="nctf-hint">
              Add one starting web page or web directory location. The crawl runs in the
              background and every email it finds is saved as a lead.
            </p>
          </header>

          <form className="nctf-form" onSubmit={handleSubmit} noValidate>
            <fieldset className="nctf-fieldset" disabled={submitting || submitStatus === 'success'}>
              <legend className="nctf-legend">Starting locations</legend>
              <ul className="nctf-list">
                {targets.map((row, index) => {
                  const inputId = `${baseId}-target-${row.key}`;
                  const errorId = `${inputId}-error`;
                  return (
                    <li
                      className="nctf-row"
                      key={row.key}
                      data-invalid={row.error ? 'true' : 'false'}
                    >
                      <div className="nctf-row-main">
                        <div className="nctf-input-wrap">
                          <span className="nctf-index" aria-hidden="true">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <input
                            id={inputId}
                            className="nctf-input"
                            aria-label={`Target ${index + 1} URL`}
                            type="url"
                            inputMode="url"
                            autoComplete="url"
                            spellCheck="false"
                            placeholder="https://example.com/team"
                            value={row.value}
                            onChange={(e) => updateTarget(row.key, e.target.value)}
                            aria-invalid={row.error ? 'true' : 'false'}
                            aria-describedby={row.error ? errorId : undefined}
                          />
                        </div>
                        {row.error ? (
                          <p className="nctf-error" id={errorId}>
                            {row.error}
                          </p>
                        ) : null}
                      </div>
                      <div className="nctf-row-controls">
                        <button
                          type="button"
                          className="nctf-remove"
                          onClick={() => removeTarget(row.key)}
                          disabled={targets.length <= 1}
                          aria-label={`Remove target ${index + 1}`}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                className="nctf-add"
                onClick={addTarget}
                disabled={targets.length >= MAX_TARGETS}
              >
                + Add another target
              </button>
            </fieldset>

            <div className="nctf-validation" role="alert" aria-live="assertive">
              {formError ? (
                <div className="nctf-alert">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                    <circle cx="10" cy="10" r="8" />
                    <path d="M10 6v5M10 13.6v.4" strokeLinecap="round" />
                  </svg>
                  <p>{formError}</p>
                </div>
              ) : null}
              {serverError ? (
                <div className="nctf-alert">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                    <circle cx="10" cy="10" r="8" />
                    <path d="M10 6v5M10 13.6v.4" strokeLinecap="round" />
                  </svg>
                  <p>{serverError}</p>
                </div>
              ) : null}
            </div>

            <div className="nctf-submit-bar">
              <button
                type="submit"
                className="nctf-submit"
                disabled={submitting || submitStatus === 'success'}
                aria-busy={submitting ? 'true' : 'false'}
              >
                <span>{submitting ? 'Queuing crawl…' : 'Start crawl'}</span>
                <svg className="nctf-submit-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 12h15M13 6l6 6-6 6" />
                </svg>
                {submitting ? <span className="nctf-progress" aria-hidden="true" /> : null}
              </button>
              <a className="nctf-cancel" href="/crawls">
                Cancel and return to Crawls
              </a>
            </div>

            <p className="nctf-status" aria-live="polite">
              {submitStatus === 'success'
                ? 'Crawl queued. Returning to Crawls…'
                : submitting
                ? `Sending ${displayedCount} to the queue…`
                : ''}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

export default NewCrawlTargetForm;